import { useEffect, useRef, useState } from "react";
import { Upload, Eye, Download, BellRing, CheckCircle2, Pencil, History, Trash2, FileSpreadsheet, FileWarning, FileText, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fmtEuro, fmtDate, fmtDateTime, isMobileDevice, f24FileUrl } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function AdminF24() {
  const [items, setItems] = useState(null);
  const [years, setYears] = useState([]);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ year: "tutti", status: "tutti", client_id: "tutti", categoria: "tutti", due_from: "", due_to: "", q: "" });
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [pendingRows, setPendingRows] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [pdfModal, setPdfModal] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ amount: "", note: "", due_date: "" });
  const [audit, setAudit] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [paying, setPaying] = useState(null);
  const fileInput = useRef(null);

  const load = () => {
    const params = {};
    if (filters.year !== "tutti") params.year = filters.year;
    if (filters.status !== "tutti") params.status = filters.status;
    if (filters.client_id !== "tutti") params.client_id = filters.client_id;
    if (filters.categoria !== "tutti") params.categoria = filters.categoria;
    if (filters.due_from) params.due_from = filters.due_from;
    if (filters.due_to) params.due_to = filters.due_to;
    if (filters.q) params.q = filters.q;
    api.get("/f24", { params }).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, [filters.year, filters.status, filters.client_id, filters.categoria, filters.due_from, filters.due_to]);
  useEffect(() => {
    api.get("/f24/years").then((r) => setYears(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadResult(null);
    setPendingRows([]);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/f24/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingRows(data.items.map((it) => ({ ...it, due_date: it.due_date || "" })));
      if (data.errors.length) setUploadResult({ created: [], errors: data.errors });
      if (data.items.length) toast.success(`${data.items.length} F24 analizzati: verifica e pubblica`);
      if (data.errors.length) toast.warning(`${data.errors.length} file non importati`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const updatePendingRow = (i, patch) =>
    setPendingRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const removePendingRow = (i) => setPendingRows((rows) => rows.filter((_, idx) => idx !== i));

  const confirmUpload = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/f24/confirm-upload", { items: pendingRows });
      setUploadResult(data);
      setPendingRows([]);
      toast.success(`${data.created.length} F24 pubblicati. Email di notifica inviata ai clienti.`);
      load();
      api.get("/f24/years").then((r) => setYears(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfirming(false);
    }
  };

  const openPdf = async (f) => {
    if (isMobileDevice()) {
      window.open(f24FileUrl(f.id), "_blank");
      return;
    }
    setPdfLoading(true);
    setPdfModal({ f, url: null });
    try {
      const r = await api.get(`/f24/${f.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setPdfModal({ f, url });
    } catch (err) {
      toast.error(formatApiError(err));
      setPdfModal(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadPdf = async (f) => {
    try {
      const r = await api.get(`/f24/${f.id}/file`, { params: { download: true }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleRemind = async (f) => {
    try {
      await api.post(`/f24/${f.id}/remind`);
      toast.success(`Reminder in invio a ${f.client_name} (email${clients.find((c) => c.id === f.client_id)?.phone ? ", SMS e WhatsApp" : ""})`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleMarkPaid = async (f) => {
    if (f.status === "pagato") {
      try {
        await api.post(`/f24/${f.id}/mark-unpaid`);
        toast.success("Pagamento annullato");
        load();
      } catch (err) {
        toast.error(formatApiError(err));
      }
      return;
    }
    setPaying({ f, date: f.due_date });
  };

  const handlePayConfirm = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/f24/${paying.f.id}/mark-paid`, { paid_at: paying.date });
      toast.success("F24 segnato come pagato");
      setPaying(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEdit = (f) => {
    setEditing(f);
    setEditForm({ amount: f.amount ?? "", note: f.note || "", due_date: f.due_date });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    try {
      const body = { note: editForm.note, due_date: editForm.due_date };
      if (editForm.amount !== "") body.amount = parseFloat(editForm.amount);
      await api.patch(`/f24/${editing.id}`, body);
      toast.success("F24 aggiornato");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openAudit = async (f) => {
    try {
      const { data } = await api.get(`/f24/${f.id}/audit`);
      setAudit({ f, entries: data });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/f24/${deleting.id}`);
      toast.success("F24 eliminato");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportFile = (fmt) => {
    const params = new URLSearchParams();
    if (filters.year !== "tutti") params.set("year", filters.year);
    if (filters.status !== "tutti") params.set("status", filters.status);
    if (filters.client_id !== "tutti") params.set("client_id", filters.client_id);
    if (filters.categoria !== "tutti") params.set("categoria", filters.categoria);
    if (filters.due_from) params.set("due_from", filters.due_from);
    if (filters.due_to) params.set("due_to", filters.due_to);
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export.${fmt}?${params.toString()}`, "_blank");
  };

  return (
    <AdminLayout title="Modelli F24" subtitle="Caricamento massivo, stati di pagamento e reminder ai clienti">
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
          <div>
            <h2 className="font-display text-lg font-bold text-navy">Caricamento massivo</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Il sistema legge il contenuto del PDF e identifica automaticamente <strong>cliente</strong> (dal codice fiscale nel documento),{" "}
              <strong>tipo di tributo</strong> (Dipendenti / Fiscale / Contributi personali) e <strong>importo</strong>.
            </p>
          </div>
          <div>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf"
              multiple
              onChange={handleUpload}
              className="hidden"
              id="f24-upload"
              data-testid="f24-mass-upload-input"
            />
            <Button
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              data-testid="f24-upload-button"
              className="bg-gold hover:bg-gold/90 text-white font-semibold"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Caricamento in corso..." : "Seleziona PDF F24"}
            </Button>
          </div>
        </div>

        {pendingRows.length > 0 && (
          <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="analyze-preview-panel">
            <div className="flex items-center justify-between bg-slate-50 px-4 py-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Verifica prima della pubblicazione</p>
              <span className="text-xs text-amber-700 font-medium">Controlla le scadenze prima di confermare</span>
            </div>
            <div className="divide-y divide-slate-100">
              {pendingRows.map((r, i) => (
                <div key={r.temp_path} className="px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`analyze-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs truncate text-muted-foreground">{r.filename}</p>
                    <p className="text-sm">
                      <strong className="text-navy">{r.client_name}</strong>
                      {" · "}
                      <span className="text-[#3C763D] font-medium">{r.categoria}</span>
                      {r.amount != null ? ` · ${fmtEuro(r.amount)}` : " · importo non rilevato"}
                    </p>
                    {r.fonte_scadenza === "periodo" && (
                      <p className="text-[11px] text-amber-700 font-medium mt-0.5">Scadenza stimata dal periodo di riferimento: verificala</p>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <Label className="text-xs">Scadenza</Label>
                      <Input
                        type="date"
                        value={r.due_date}
                        onChange={(e) => updatePendingRow(i, { due_date: e.target.value })}
                        className={`w-44 ${!r.due_date ? "border-red-400 bg-red-50" : ""}`}
                        data-testid={`analyze-due-date-${i}`}
                      />
                      {!r.due_date && <p className="text-[11px] text-red-600 mt-0.5">Non rilevata: inseriscila manualmente</p>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removePendingRow(i)} title="Rimuovi" data-testid={`analyze-remove-${i}`}>
                      <X className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-slate-50 px-4 py-3 flex flex-col sm:flex-row items-center justify-end gap-3">
              {pendingRows.some((r) => !r.due_date) && (
                <p className="text-xs text-red-600">Inserisci la scadenza mancante per pubblicare</p>
              )}
              <Button
                onClick={confirmUpload}
                disabled={confirming || pendingRows.some((r) => !r.due_date)}
                data-testid="confirm-upload-button"
                className="bg-navy hover:bg-navy-light text-white"
              >
                {confirming ? "Pubblicazione in corso..." : `Conferma e pubblica ${pendingRows.length} F24`}
              </Button>
            </div>
          </div>
        )}

        {uploadResult && (
          <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="upload-result-panel">
            <div className="flex items-center justify-between bg-slate-50 px-4 py-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Esito importazione</p>
              <button onClick={() => setUploadResult(null)} className="text-muted-foreground hover:text-navy" data-testid="upload-result-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-slate-100 text-sm">
              {uploadResult.created.map((c, i) => (
                <div key={`c-${i}`} className="px-4 py-2.5 flex items-center gap-3">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="font-mono text-xs">{c.filename}</span>
                  <span className="text-muted-foreground">→ {c.client} · {c.categoria || "Tipo n/d"}{c.importo != null ? ` · ${fmtEuro(c.importo)}` : ""} · scadenza {fmtDate(c.scadenza)}</span>
                </div>
              ))}
              {uploadResult.errors.map((er, i) => (
                <div key={`e-${i}`} className="px-4 py-2.5 flex items-center gap-3">
                  <FileWarning className="w-4 h-4 text-red-600 shrink-0" />
                  <span className="font-mono text-xs">{er.filename}</span>
                  <span className="text-red-700">{er.reason}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="w-36">
          <Label className="text-xs">Anno</Label>
          <Select value={filters.year} onValueChange={(v) => setFilters({ ...filters, year: v })}>
            <SelectTrigger data-testid="filter-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Label className="text-xs">Stato</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti</SelectItem>
              <SelectItem value="in_attesa">In attesa di conferma</SelectItem>
              <SelectItem value="autorizzato">Autorizzato</SelectItem>
              <SelectItem value="pagato">Pagato</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-52">
          <Label className="text-xs">Cliente</Label>
          <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v })}>
            <SelectTrigger data-testid="filter-client-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti i clienti</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Label className="text-xs">Tipo delega</Label>
          <Select value={filters.categoria} onValueChange={(v) => setFilters({ ...filters, categoria: v })}>
            <SelectTrigger data-testid="filter-categoria-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutte</SelectItem>
              <SelectItem value="Dipendenti">Dipendenti</SelectItem>
              <SelectItem value="Fiscale">Fiscale</SelectItem>
              <SelectItem value="Contributi personali">Contributi personali</SelectItem>
              <SelectItem value="Altro">Altro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Scadenza da</Label>
          <Input type="date" value={filters.due_from} onChange={(e) => setFilters({ ...filters, due_from: e.target.value })} className="w-40" data-testid="filter-due-from" />
        </div>
        <div>
          <Label className="text-xs">a</Label>
          <Input type="date" value={filters.due_to} onChange={(e) => setFilters({ ...filters, due_to: e.target.value })} className="w-40" data-testid="filter-due-to" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Cerca</Label>
          <Input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && load()}
            onBlur={load}
            placeholder="Cliente, CF o nome file..."
            data-testid="filter-search-input"
          />
        </div>
        <Button variant="outline" onClick={() => exportFile("xlsx")} data-testid="export-excel-button">
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Excel
        </Button>
        <Button variant="outline" onClick={() => exportFile("pdf")} data-testid="export-pdf-button">
          <FileText className="w-4 h-4 mr-2" />
          PDF
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
        {items === null ? (
          <p className="text-sm text-muted-foreground p-12 text-center">Caricamento...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground p-12 text-center" data-testid="f24-empty-state">Nessun F24 trovato con i filtri selezionati.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="f24-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-slate-100 bg-slate-50/60">
                  <th className="px-5 py-3 font-semibold">Cliente</th>
                  <th className="px-5 py-3 font-semibold">File</th>
                  <th className="px-5 py-3 font-semibold">Tipo</th>
                  <th className="px-5 py-3 font-semibold">Scadenza</th>
                  <th className="px-5 py-3 font-semibold text-right">Importo</th>
                  <th className="px-5 py-3 font-semibold">Stato</th>
                  <th className="px-5 py-3 font-semibold text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {items.map((f) => (
                  <tr key={f.id} className="hover:bg-slate-50/60 transition-colors duration-150" data-testid={`f24-row-${f.id}`}>
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-navy">{f.client_name}</p>
                      <p className="font-mono text-[11px] text-muted-foreground">{f.codice_fiscale}</p>
                    </td>
                    <td className="px-5 py-3.5 text-muted-foreground max-w-[200px] truncate" title={f.original_filename}>{f.original_filename}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-[#3C763D] border border-green-200">{f.categoria || "—"}</span>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">{fmtDate(f.due_date)}</td>
                    <td className="px-5 py-3.5 text-right font-medium whitespace-nowrap">{f.amount != null ? fmtEuro(f.amount) : "—"}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={f.status} /></td>
                    <td className="px-5 py-3.5">
                      <div className="flex justify-end gap-0.5">
                        <Button variant="ghost" size="sm" onClick={() => openPdf(f)} title="Visualizza PDF" data-testid={`f24-view-${f.id}`}><Eye className="w-4 h-4 text-navy" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadPdf(f)} title="Scarica PDF" data-testid={`f24-download-${f.id}`}><Download className="w-4 h-4 text-navy" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleRemind(f)} title="Invia reminder" data-testid={`f24-remind-${f.id}`}><BellRing className="w-4 h-4 text-gold" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(f)} title={f.status === "pagato" ? "Annulla pagamento" : "Segna come pagato"} data-testid={`f24-mark-paid-${f.id}`}>
                          <CheckCircle2 className={`w-4 h-4 ${f.status === "pagato" ? "text-emerald-600" : "text-slate-400"}`} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(f)} title="Modifica importo/note" data-testid={`f24-edit-${f.id}`}><Pencil className="w-4 h-4 text-navy" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => openAudit(f)} title="Registro autorizzazioni" data-testid={`f24-audit-${f.id}`}><History className="w-4 h-4 text-navy" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleting(f)} title="Elimina" data-testid={`f24-delete-${f.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!pdfModal} onOpenChange={() => { if (pdfModal?.url) URL.revokeObjectURL(pdfModal.url); setPdfModal(null); }}>
        <DialogContent className="max-w-4xl" data-testid="pdf-viewer-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy text-base">{pdfModal?.f?.original_filename}</DialogTitle>
          </DialogHeader>
          {pdfLoading || !pdfModal?.url ? (
            <p className="text-sm text-muted-foreground py-24 text-center">Caricamento PDF...</p>
          ) : (
            <iframe src={pdfModal.url} title="Anteprima F24" className="w-full h-[70vh] rounded-lg border border-slate-200" data-testid="pdf-viewer-iframe" />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="f24-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Modifica F24</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="e-amount">Importo (€)</Label>
              <Input id="e-amount" type="number" step="0.01" min="0" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} data-testid="f24-amount-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="e-due">Data di scadenza</Label>
              <Input id="e-due" type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} data-testid="f24-due-date-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="e-note">Note</Label>
              <Input id="e-note" value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} data-testid="f24-note-input" />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="f24-edit-save-button" className="bg-navy hover:bg-navy-light text-white">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!audit} onOpenChange={() => setAudit(null)}>
        <DialogContent data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Registro autorizzazioni</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground font-mono">{audit?.f?.original_filename}</p>
          {audit?.entries.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nessuna autorizzazione registrata.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm max-h-80 overflow-y-auto">
              {audit?.entries.map((a) => (
                <li key={a.id} className="py-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold ${a.action === "autorizzato" ? "text-blue-700" : "text-amber-700"}`}>
                      {a.action === "autorizzato" ? "Autorizzazione al pagamento" : "Revoca autorizzazione"}
                    </span>
                    <span className="text-xs text-muted-foreground">{fmtDateTime(a.timestamp)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Utente: {a.user_email} · IP: {a.ip}</p>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!paying} onOpenChange={() => setPaying(null)}>
        <DialogContent data-testid="f24-mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Segna come pagato</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePayConfirm} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Confermi la data di pagamento dell'F24. È preimpostata alla data di scadenza ma puoi modificarla se il pagamento è avvenuto in un giorno diverso.
            </p>
            <div className="space-y-2">
              <Label htmlFor="pay-date">Data di pagamento</Label>
              <Input id="pay-date" type="date" required value={paying?.date || ""} onChange={(e) => setPaying({ ...paying, date: e.target.value })} data-testid="f24-paid-date-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPaying(null)} data-testid="f24-paid-cancel-button">Annulla</Button>
              <Button type="submit" data-testid="f24-paid-save-button" className="bg-navy hover:bg-navy-light text-white">Conferma pagamento</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent data-testid="f24-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo F24?</AlertDialogTitle>
            <AlertDialogDescription>{deleting?.original_filename} — non sarà più visibile al cliente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="f24-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="f24-delete-confirm" className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
